import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { User, Vendor } from '@completion/models';
import { projectUsers } from '@completion/selectors';
import { Subject } from 'rxjs';
import { takeUntil, startWith, map } from 'rxjs/operators';
import { UntypedFormControl } from '@angular/forms';
import { ProjectUsers } from '@completion/actions';

@Component({
  selector: 'app-add-users-dialog',
  templateUrl: './add-users-dialog.component.html',
  styleUrls: ['./add-users-dialog.component.scss'],
  standalone: false,
})
export class AddUsersDialogComponent implements OnInit, OnDestroy {
  unmappedUsers: User[] = [];
  mappedUsers: User[] = [];
  filteredUnmappedUsers: User[] = [];
  searchActive = false;

  /** Track IDs that were originally assigned so we can detect changes & highlight new ones */
  private originalMappedIds = new Set<number>();

  unmappedSearchControl = new UntypedFormControl('');

  private destroy$ = new Subject<void>();

  constructor(
    private store: Store,
    private dialogRef: MatDialogRef<AddUsersDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { vendor: Vendor },
  ) {
    const vendorUsers = Array.isArray(data.vendor.users) ? data.vendor.users : [];
    this.originalMappedIds = new Set(vendorUsers.map((u) => u.id));

    this.store
      .select(projectUsers)
      .pipe(takeUntil(this.destroy$))
      .subscribe((users) => {
        this.unmappedUsers = users.filter((user) => !vendorUsers.some((vUser) => vUser.id === user.id));
        this.mappedUsers = [...vendorUsers];
        this.applyUnmappedFilter();
      });

    this.unmappedSearchControl.valueChanges
      .pipe(
        startWith(''),
        map((value) => (typeof value === 'string' ? value : '')),
      )
      .subscribe((searchTerm) => {
        const trimmed = searchTerm.trim();
        this.searchActive = trimmed.length >= 2;
        if (!this.searchActive) {
          this.filteredUnmappedUsers = [];
          return;
        }
        const lower = trimmed.toLowerCase();
        this.filteredUnmappedUsers = this.unmappedUsers.filter((user) => (user.name || user.userName || '').toLowerCase().includes(lower));
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    this.store.dispatch(new ProjectUsers());
  }

  getInitials(user: User): string {
    const source = user?.name || user?.userName || '';
    return source
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0].toUpperCase())
      .join('');
  }

  displayUser(_user: User): string {
    return '';
  }

  isNewlyAdded(user: User): boolean {
    return !this.originalMappedIds.has(user.id);
  }

  hasChanges(): boolean {
    const currentIds = new Set(this.mappedUsers.map((u) => u.id));
    if (currentIds.size !== this.originalMappedIds.size) return true;
    for (const id of currentIds) {
      if (!this.originalMappedIds.has(id)) return true;
    }
    return false;
  }

  addUser(user: User): void {
    if (this.mappedUsers.some((u) => u.id === user.id)) return;
    this.mappedUsers = [...this.mappedUsers, user];
    this.unmappedUsers = this.unmappedUsers.filter((u) => u.id !== user.id);
    this.applyUnmappedFilter();
  }

  removeUser(user: User): void {
    this.mappedUsers = this.mappedUsers.filter((u) => u.id !== user.id);
    if (!this.unmappedUsers.some((u) => u.id === user.id)) {
      this.unmappedUsers = [...this.unmappedUsers, user];
    }
    this.applyUnmappedFilter();
  }

  save(): void {
    this.dialogRef.close(this.mappedUsers);
  }

  cancel(): void {
    this.dialogRef.close();
  }

  private applyUnmappedFilter(): void {
    const raw = typeof this.unmappedSearchControl.value === 'string' ? this.unmappedSearchControl.value : '';
    const trimmed = raw.trim();
    this.searchActive = trimmed.length >= 2;
    if (!this.searchActive) {
      this.filteredUnmappedUsers = [];
      return;
    }
    const lower = trimmed.toLowerCase();
    this.filteredUnmappedUsers = this.unmappedUsers.filter((user) => (user.name || user.userName || '').toLowerCase().includes(lower));
  }
}
